import React, { useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Box, Flex, Button } from '@qga/roo-ui/components';
import OfferCheckoutLink from './OfferCheckoutLink';
import OfferPayableAtProperty from './OfferPayableAtProperty';
import { PAYMENT_METHODS } from 'lib/enums/payment';
import { getQueryPayWith } from 'store/router/routerSelectors';
import PointsPaySliderForm from 'components/PointsPaySlider/PointsPaySliderForm';
import { Decimal } from 'decimal.js';
import { useMount } from 'react-use';

const OfferPointsPaySlider = ({
  hasAvailability,
  onCheckAvailabilityClicked,
  totalCash,
  totalPointsAmount,
  payableAtProperty,
  propertyId,
  offerId,
  offerInstanceId,
  roomTypeId,
  offerName,
  roomName,
  onCashPaymentAmountChange,
  exclusiveOffer = false,
}) => {
  const { totalAmountPayableNow, totalCashAmount } = useMemo(() => {
    const payableAtPropertyAmount = payableAtProperty?.currency === 'AUD' ? payableAtProperty?.amount : '0';
    const totalAmountPayableNow = payableAtPropertyAmount ? totalCash.amount - payableAtPropertyAmount : totalCash.amount;
    const totalCashAmount = new Decimal(totalAmountPayableNow);

    return { totalAmountPayableNow, totalCashAmount };
  }, [payableAtProperty?.amount, payableAtProperty?.currency, totalCash.amount]);

  const queryPayWith = useSelector(getQueryPayWith);
  const [initialCash, setInitialCash] = useState(totalAmountPayableNow);
  const [payWith, setPayWith] = useState(queryPayWith);

  useMount(() => {
    if (typeof onCashPaymentAmountChange === 'function') onCashPaymentAmountChange({ cashAmount: totalCashAmount });
  });

  const updatePointsAndCash = useCallback(
    ({ cash, points }) => {
      const cashAmount = cash?.amount;
      const pointsAmount = points?.amount;
      const payWith = pointsAmount?.greaterThan(0) ? PAYMENT_METHODS.POINTS : PAYMENT_METHODS.CASH;

      if (typeof onCashPaymentAmountChange === 'function') onCashPaymentAmountChange({ cashAmount });
      setInitialCash(cashAmount?.toNumber());
      setPayWith(payWith);
    },
    [onCashPaymentAmountChange],
  );

  return (
    <Box
      backgroundColor="greys.porcelain"
      my={exclusiveOffer && !hasAvailability ? [2, 0, 1] : [6, 1]}
      p={4}
      data-testid="points-pay-wrapper"
    >
      <Flex flexDirection="column">
        <PointsPaySliderForm
          showLoginBanner
          totalCashAmount={totalCashAmount}
          totalPointsAmount={totalPointsAmount}
          updatePointsAndCash={updatePointsAndCash}
          offerInstanceId={offerInstanceId}
          data-testid="points-pay-slider-form"
        />
      </Flex>
      {payableAtProperty && <OfferPayableAtProperty payableAtProperty={payableAtProperty} isIncludedInTotal={false} />}
      {hasAvailability ? (
        <OfferCheckoutLink
          propertyId={propertyId}
          roomTypeId={roomTypeId}
          offerId={offerId}
          initialCash={initialCash}
          offerName={offerName}
          roomName={roomName}
          payWith={payWith}
          data-testid="slider-checkout-link"
          mt={5}
        />
      ) : (
        <Button
          variant="primary"
          mt={6}
          width={exclusiveOffer ? ['100%'] : ['100%', '90%', '100%', '80%']}
          onClick={onCheckAvailabilityClicked}
          data-testid="check-availability-cta"
        >
          Check Availability
        </Button>
      )}
    </Box>
  );
};

OfferPointsPaySlider.propTypes = {
  label: PropTypes.node,
  totalCash: PropTypes.shape({
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    currency: PropTypes.string.isRequired,
  }).isRequired,
  totalPointsAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  payableAtProperty: PropTypes.shape({
    amount: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
  }).isRequired,
  propertyId: PropTypes.string.isRequired,
  roomTypeId: PropTypes.string.isRequired,
  offerId: PropTypes.string.isRequired,
  offerInstanceId: PropTypes.string,
  offerName: PropTypes.string.isRequired,
  roomName: PropTypes.string.isRequired,
  onCashPaymentAmountChange: PropTypes.func.isRequired,
  campaignPriceMessage: PropTypes.node,
  hasAvailability: PropTypes.bool.isRequired,
  onCheckAvailabilityClicked: PropTypes.func,

  exclusiveOffer: PropTypes.bool,
};

export default OfferPointsPaySlider;
